import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, memo } from 'react'
import {
  Award,
  Users,
  MapPin,
  Sparkles,
  Briefcase,
  GraduationCap,
  Settings,
  Lightbulb,
  TrendingUp,
  Building,
  Hotel,
  Palmtree,
  Droplets,
  Target,
  History,
  Heart,
  Globe,
  X,
  ArrowRight,
  BookOpen,
} from 'lucide-react'
import { ContactCTA } from '@/components/sections'
import { LocalizedLink } from '@/components/LocalizedLink'
import chiSiamoImg from '@/assets/images/sections/ChiSiamo.png'

// Reality cards configuration
const realityCards = [
  {
    id: 'story',
    icon: History,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
    accentColor: 'text-amber-600',
    hoverBorder: 'hover:border-amber-300',
  },
  {
    id: 'team',
    icon: Users,
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-50',
    accentColor: 'text-blue-600',
    hoverBorder: 'hover:border-blue-300',
  },
  {
    id: 'values',
    icon: Heart,
    color: 'from-rose-500 to-pink-500',
    bgColor: 'bg-rose-50',
    accentColor: 'text-rose-600',
    hoverBorder: 'hover:border-rose-300',
  },
  {
    id: 'presence',
    icon: Globe,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    accentColor: 'text-emerald-600',
    hoverBorder: 'hover:border-emerald-300',
  },
]

// Mission services
const managementServices = [
  { icon: Palmtree, key: 'resort' },
  { icon: Building, key: 'urban' },
  { icon: Hotel, key: 'hotel' },
  { icon: Target, key: 'destination' },
  { icon: Droplets, key: 'mineral' },
]

const consultingServices = [
  { icon: Users, key: 'recruiting' },
  { icon: Settings, key: 'management' },
  { icon: Briefcase, key: 'business' },
  { icon: Lightbulb, key: 'newopening' },
  { icon: TrendingUp, key: 'design' },
]

// Company Badge
const CompanyBadge = memo(function CompanyBadge({ text }: { text: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-900 to-gray-700 text-white px-4 py-2 rounded-full shadow-lg"
    >
      <Award className="w-5 h-5" />
      <span className="font-semibold tracking-wide">{text}</span>
    </motion.div>
  )
})

// Value Pillars Component
const ValuePillars = memo(function ValuePillars({ t }: { t: (key: string) => string }) {
  const pillars = [
    { key: 'quality', icon: Award, color: 'from-amber-500 to-orange-500' },
    { key: 'professionalism', icon: Briefcase, color: 'from-blue-500 to-indigo-500' },
    { key: 'innovation', icon: Lightbulb, color: 'from-emerald-500 to-teal-500' },
  ]

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {pillars.map((pillar, index) => {
        const Icon = pillar.icon
        return (
          <motion.div
            key={pillar.key}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className="relative group"
          >
            <div className="relative overflow-hidden rounded-2xl bg-white/80 backdrop-blur-sm border border-white/50 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
              <div
                className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br ${pillar.color} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`}
              />
              <div
                className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-4 shadow-lg`}
              >
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t(`values.pillars.${pillar.key}.title`)}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t(`values.pillars.${pillar.key}.description`)}
              </p>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
})

// Reality Card Component
const RealityCard = memo(function RealityCard({
  card,
  index,
  onClick,
  t,
}: {
  card: (typeof realityCards)[0]
  index: number
  onClick: () => void
  t: (key: string) => string
}) {
  const Icon = card.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={onClick}
      className="relative group cursor-pointer h-full"
    >
      <div
        className={`relative overflow-hidden rounded-xl sm:rounded-2xl bg-white border-2 border-gray-100 ${card.hoverBorder} p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2 h-full flex flex-col`}
      >
        {/* Background gradient orb */}
        <div
          className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${card.color} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`}
        />

        {/* Icon */}
        <div
          className={`relative w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-3 sm:mb-5 shadow-lg group-hover:scale-110 transition-transform`}
        >
          <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
        </div>

        {/* Content */}
        <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
          {t(`reality.tabs.${card.id}.title`)}
        </h3>
        <p className={`${card.accentColor} font-medium text-xs sm:text-sm mb-2 sm:mb-3`}>
          {t(`reality.tabs.${card.id}.subtitle`)}
        </p>
        <p className="text-gray-600 text-xs sm:text-sm leading-relaxed flex-1 line-clamp-3 sm:line-clamp-none">
          {t(`reality.tabs.${card.id}.description`)}
        </p>

        {/* Click indicator */}
        <div
          className={`mt-3 sm:mt-4 inline-flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium ${card.accentColor} group-hover:gap-3 transition-all`}
        >
          <span className="hidden sm:inline">{t('reality.discover_more')}</span>
          <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
        </div>
      </div>
    </motion.div>
  )
})

// Reality Modal Component
const RealityModal = memo(function RealityModal({
  card,
  onClose,
  t,
}: {
  card: (typeof realityCards)[0] | null
  onClose: () => void
  t: (key: string, options?: object) => string | string[]
}) {
  if (!card) return null

  const Icon = card.icon
  const highlights = t(`reality.tabs.${card.id}.highlights`, { returnObjects: true }) as string[]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={`relative w-full max-w-2xl ${card.bgColor} rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-0`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md transition-colors"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div
            className={`w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}
          >
            <Icon className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
              {t(`reality.tabs.${card.id}.title`) as string}
            </h3>
            <p className={`text-sm sm:text-base ${card.accentColor} font-semibold`}>
              {t(`reality.tabs.${card.id}.subtitle`) as string}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-700 leading-relaxed text-base sm:text-lg mb-6 sm:mb-8">
          {t(`reality.tabs.${card.id}.description`) as string}
        </p>

        {/* Highlights */}
        <div className="space-y-3 sm:space-y-4">
          {Array.isArray(highlights) &&
            highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-white/80 shadow-sm"
              >
                <div
                  className={`flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br ${card.color} flex items-center justify-center`}
                >
                  <span className="text-white font-bold text-xs sm:text-sm">{index + 1}</span>
                </div>
                <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{highlight}</span>
              </motion.div>
            ))}
        </div>

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 sm:mt-8 inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-3 rounded-full bg-white shadow-md"
        >
          <MapPin className={`w-4 h-4 sm:w-5 sm:h-5 ${card.accentColor}`} />
          <span className="text-sm sm:text-base font-medium text-gray-700">
            {t(`reality.tabs.${card.id}.badge`) as string}
          </span>
        </motion.div>
      </motion.div>
    </motion.div>
  )
})

export function AboutUs() {
  const { t, i18n } = useTranslation('about')
  const [selectedCard, setSelectedCard] = useState<(typeof realityCards)[0] | null>(null)

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      {/* Hero Section with Parallax Effect */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={chiSiamoImg}
            alt="BF Wellness"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-64 h-64 rounded-full bg-white/5"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
              }}
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 20}%`,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative container-custom py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <CompanyBadge text={t('hero.badge')} />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-white mt-6 mb-6"
            >
              {t('hero.title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Keywords */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {(t('hero.tags', { returnObjects: true }) as string[]).map((word) => (
                <span
                  key={word}
                  className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold text-sm"
                >
                  {word}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 rounded-full bg-white/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* Interactive Reality Section - Card Grid */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 opacity-50 blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 opacity-50 blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('reality.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('reality.subtitle')}</p>
          </motion.div>

          {/* Card Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {realityCards.map((card, index) => (
              <RealityCard
                key={card.id}
                card={card}
                index={index}
                onClick={() => setSelectedCard(card)}
                t={t as (key: string) => string}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedCard && (
          <RealityModal
            card={selectedCard}
            onClose={() => setSelectedCard(null)}
            t={t as (key: string, options?: object) => string | string[]}
          />
        )}
      </AnimatePresence>

      {/* Values Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('values.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('values.subtitle')}</p>
          </motion.div>

          <ValuePillars t={t as (key: string) => string} />
        </div>
      </section>

      {/* Mission Section - Management & Consulting */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('mission.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('mission.description')}</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
            {/* Management Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100"
            >
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                  <Settings className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {t('mission.management.title')}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">{t('mission.management.subtitle')}</p>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3">
                {managementServices.map((service, index) => {
                  const Icon = service.icon
                  return (
                    <motion.div
                      key={service.key}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-md sm:rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors"
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                      <span className="text-sm sm:text-base text-gray-700 font-medium">
                        {t(`mission.management.services.${service.key}`)}
                      </span>
                    </motion.div>
                  )
                })}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="mt-4 sm:mt-6"
              >
                <LocalizedLink
                  to="management"
                  className="inline-flex items-center gap-2 px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  {t('mission.management.cta')}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </LocalizedLink>
              </motion.div>
            </motion.div>

            {/* Consulting Column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100"
            >
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
                  <GraduationCap className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {t('mission.consulting.title')}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">{t('mission.consulting.subtitle')}</p>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3">
                {consultingServices.map((service, index) => {
                  const Icon = service.icon
                  return (
                    <motion.div
                      key={service.key}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-md sm:rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      <span className="text-sm sm:text-base text-gray-700 font-medium">
                        {t(`mission.consulting.services.${service.key}`)}
                      </span>
                    </motion.div>
                  )
                })}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="mt-4 sm:mt-6"
              >
                <LocalizedLink
                  to="consulting"
                  className="inline-flex items-center gap-2 px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  {t('mission.consulting.cta')}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </LocalizedLink>
              </motion.div>
            </motion.div>
          </div>

          {/* Training Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-4 sm:mt-8 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                  <BookOpen className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {t('mission.training.title')}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">{t('mission.training.subtitle')}</p>
                </div>
              </div>
              <p className="text-sm sm:text-base text-gray-600 md:max-w-md">{t('mission.training.description')}</p>
              <LocalizedLink
                to="training"
                className="inline-flex items-center gap-2 px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                {t('mission.training.cta')}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </LocalizedLink>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactCTA />
    </>
  )
}
